import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'
import ApprovalBudget from '../components/budget/ApprovalBudget'
import { isPmOf } from '../utils/pm'
import { computeLine, lineTotals } from '../utils/purchaseTotals'
import { externalHref } from '../utils/url'
import CopiarFormatoSap from '../components/purchases/CopiarFormatoSap'
import { currentLocale } from '../i18n'
import StatusPill from '../components/StatusPill'
import MentionTextarea from '../components/menciones/MentionTextarea'
import TextoConMenciones from '../components/menciones/TextoConMenciones'
import {
  FULFILLMENT_STATUSES, FULFILLMENT_STYLE, PAYMENT_STATUSES, PAYMENT_STYLE,
} from '../utils/purchaseStatus'
import {
  getPurchase, deletePurchase,
  submitPurchase, approvePurchase, rejectPurchase, cancelPurchase,
  sapPurchase, orderPurchase, receivePurchase, closePurchase, updatePurchaseOrderFields,
  uploadPurchaseAttachment, downloadPurchaseAttachment, deletePurchaseAttachment,
  exportPurchaseExcel,
  getPurchaseComments, addPurchaseComment, getTaggableUsers, getPurchaseEdits,
  getProjects, getFormatoSapSolicitud,
} from '../api/client'

const ORDER_TYPE_BADGE = {
  national: 'bg-emerald-100 text-emerald-700',
  international: 'bg-amber-100 text-amber-700',
  service: 'bg-violet-100 text-violet-700',
}


const STATE_STYLE = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-amber-100 text-amber-700',
  approved:  'bg-blue-100 text-blue-700',
  sap:       'bg-violet-100 text-violet-700',
  rejected:  'bg-red-100 text-red-700',
  cancelled: 'bg-orange-100 text-orange-700',
  ordered:   'bg-indigo-100 text-indigo-700',
  received:  'bg-emerald-100 text-emerald-700',
  closed:    'bg-gray-200 text-gray-700',
}

function fmtBytes(n) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function fmtMoney(n, currency) {
  if (n == null || isNaN(n)) return '—'
  return `${Number(n).toLocaleString(currentLocale(), { maximumFractionDigits: 2 })} ${currency}`
}

function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(currentLocale(), {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <div className="text-sm text-gray-900">{children}</div>
    </div>
  )
}

export default function PurchaseDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { t } = useTranslation(['purchases', 'common'])
  const roles = user?.roles || (user?.role ? [user.role] : [])
  const isAdmin = roles.includes('admin')
  const isPurchaser = isAdmin || roles.includes('purchaser')

  const [req, setReq] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [projects, setProjects] = useState([])
  const [editing, setEditing] = useState(false)
  const [orderForm, setOrderForm] = useState({
    po_number: '', final_unit_price: '', supplier: '', eta: '', tracking: '',
    fulfillment_status: '', purchase_date: '', leadtime_weeks: '', payment_status: 'pending',
  })
  const [activeTab, setActiveTab] = useState('data')
  const [rejectModal, setRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [cancelModal, setCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [sapModal, setSapModal] = useState(false)
  const [sapPo, setSapPo] = useState('')
  const fileRef = useRef(null)

  const [edits, setEdits] = useState([])

  const [comments, setComments] = useState([])
  const [taggableUsers, setTaggableUsers] = useState([])
  const [commentBody, setCommentBody] = useState('')
  const [commentTag, setCommentTag] = useState('')
  const [postingComment, setPostingComment] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const data = await getPurchase(id)
      setReq(data)
      getPurchaseEdits(id).then(d => setEdits(Array.isArray(d) ? d : [])).catch(() => setEdits([]))
      setError('')
    } catch (e) {
      setError(e.response?.data?.detail || t('detail.errors.loadFailed'))
      setReq(null)
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [id])
  useEffect(() => {
    getProjects().then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (!req || !['approved', 'sap', 'ordered', 'received', 'closed'].includes(req.state)) return
    getPurchaseComments(req.id).then(d => setComments(Array.isArray(d) ? d : [])).catch(() => {})
    getTaggableUsers().then(d => setTaggableUsers(Array.isArray(d) ? d : [])).catch(() => {})
  }, [req?.id, req?.state])

  if (loading) return <div className="p-8 text-sm text-gray-400">{t('common:actions.loading')}</div>
  if (error)   return <div className="p-8 text-sm text-red-600">{error}</div>
  if (!req)    return <div className="p-8 text-sm text-gray-400">{t('detail.notFound')}</div>

  const isRequester = user?.id === req.requester_id
  // Approve/reject/cancel must come from the PM/PO assigned to this purchase's
  // project (or an admin) — the global pm_po role alone is not enough.
  const reqProject = projects.find(p => p.name === req.project_reference)
  const isProjectPm = isPmOf(user, reqProject)
  const canDecide = isAdmin || isProjectPm
  // Purchaser/admin may full-edit approved-onward (matches the purchaser view gate);
  // admins may edit any state; a plain requester only while draft.
  const APPROVED_ONWARD = ['approved', 'sap', 'ordered', 'received', 'closed']
  const pagada = req.payment_status === 'paid'      // a paid request is closed to changes (the server refuses them too)
  const canFullEdit = !pagada && (isAdmin
    || (isRequester && req.state === 'draft')
    || (isPurchaser && APPROVED_ONWARD.includes(req.state)))
  const canDelete = isAdmin || (isRequester && req.state === 'draft')
  const canSubmit = (isRequester || isAdmin) && req.state === 'draft'
  const canApproveReject = canDecide && req.state === 'submitted'
  const canCancel = canDecide && req.state === 'approved'
  const canSendToSap = isPurchaser && req.state === 'approved'
  const canOrder = isPurchaser && req.state === 'sap'
  const canEditOrderFields = isPurchaser && APPROVED_ONWARD.includes(req.state)
  const canReceive = isPurchaser && req.state === 'ordered'
  const canClose = isPurchaser && req.state === 'received'
  const canAttach = (
    (isRequester && req.state === 'draft')
    || isAdmin
    || (isPurchaser && ['approved', 'sap', 'ordered', 'received', 'closed'].includes(req.state))
  )

  // Order/logistics info exists only from approval onward.
  const hasOrderInfo = APPROVED_ONWARD.includes(req.state)
  // Edit is a single global mode for the whole purchase, not per-tab: pressing
  // "Edit" opens every field the user may change (across tabs), and one "Save"
  // commits them all together. This avoids the trap of thinking you saved the
  // whole purchase when only one tab's fields went through.
  const canEditData = canFullEdit
  const canEditOrder = canEditOrderFields && hasOrderInfo
  // Switching tabs keeps the global edit mode alive, so nothing typed is lost.
  const switchTab = (tab) => setActiveTab(tab)


  const startEdit = () => {
    // Seed the order/logistics form too, so the same global edit covers both tabs.
    setOrderForm({
      po_number: req.po_number || '',
      final_unit_price: req.final_unit_price ?? '',
      supplier: req.supplier || '',
      eta: req.eta || '',
      tracking: req.tracking || '',
      fulfillment_status: req.fulfillment_status || '',
      purchase_date: req.purchase_date || '',
      leadtime_weeks: req.leadtime_weeks ?? '',
      payment_status: req.payment_status || 'pending',
    })
    setEditing(true)
  }
  const cancelEdit = () => { setEditing(false); setError('') }

  const guard = async (fn) => {
    setBusy(true); setError('')
    try { await fn() } catch (e) { setError(e.response?.data?.detail || t('detail.errors.actionFailed')) }
    setBusy(false)
  }

  // One save for the whole purchase: it commits the request-data fields and the
  // order/logistics fields together (whichever the user is allowed to change),
  // so "Save" always means "the entire purchase is saved".
  const saveAll = () => guard(async () => {
    let updated = null
    if (canEditOrder) {
      const orderPayload = {
        final_unit_price: orderForm.final_unit_price === '' ? null : Number(orderForm.final_unit_price),
        eta: orderForm.eta || null,
        po_number: orderForm.po_number || null,
        supplier: orderForm.supplier || null,
        tracking: orderForm.tracking || null,
        fulfillment_status: req.state === 'ordered' ? (orderForm.fulfillment_status || null) : null,
        purchase_date: orderForm.purchase_date || null,
        leadtime_weeks: orderForm.leadtime_weeks === '' ? null : Number(orderForm.leadtime_weeks),
        payment_status: orderForm.payment_status || 'pending',
      }
      updated = await updatePurchaseOrderFields(req.id, orderPayload)
    }
    if (updated) setReq(updated)
    setEditing(false)
    getPurchaseEdits(req.id).then(d => setEdits(Array.isArray(d) ? d : [])).catch(() => {})
  })

  const doDelete = () => guard(async () => {
    if (!confirm(t('detail.comments.deleteConfirm'))) return
    await deletePurchase(req.id)
    navigate('/purchases')
  })

  const doSubmit = () => guard(async () => setReq(await submitPurchase(req.id)))
  const doApprove = () => guard(async () => setReq(await approvePurchase(req.id)))
  const doSendToSap = () => guard(async () => {
    setReq(await sapPurchase(req.id, sapPo.trim()))
    setSapModal(false); setSapPo('')
  })
  const doReject = () => guard(async () => {
    setReq(await rejectPurchase(req.id, rejectReason || null))
    setRejectModal(false); setRejectReason('')
  })
  const doCancel = () => guard(async () => {
    setReq(await cancelPurchase(req.id, cancelReason.trim()))
    setCancelModal(false); setCancelReason('')
  })
  // Pure state transition sap → ordered. Order fields are edited through the
  // global Edit/Save, so this just moves the workflow forward.
  const doOrder = () => guard(async () => { setReq(await orderPurchase(req.id)) })
  const doReceive = () => guard(async () => setReq(await receivePurchase(req.id)))
  const doClose = () => guard(async () => setReq(await closePurchase(req.id)))
  const doDownload = () => guard(async () => { await exportPurchaseExcel(req.id) })

  const onPickFile = () => fileRef.current?.click()

  const postComment = async () => {
    const body = commentBody.trim()
    if (!body) return
    setPostingComment(true); setError('')
    try {
      await addPurchaseComment(req.id, { body, tagged_user_id: commentTag ? Number(commentTag) : null })
      setCommentBody(''); setCommentTag('')
      const fresh = await getPurchaseComments(req.id)
      setComments(Array.isArray(fresh) ? fresh : [])
    } catch (e) {
      setError(e.response?.data?.detail || t('detail.errors.commentFailed'))
    } finally {
      setPostingComment(false)
    }
  }

  const onFileChange = async (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    e.target.value = ''
    await guard(async () => {
      await uploadPurchaseAttachment(req.id, f)
      await load()
    })
  }
  const removeAttachment = (att) => guard(async () => {
    if (!confirm(t('detail.attachments.removeConfirm', { filename: att.filename }))) return
    await deletePurchaseAttachment(req.id, att.id)
    await load()
  })

  return (
    <div className="p-8 max-w-5xl">
      <button onClick={() => navigate('/purchases')} className="text-xs text-gray-500 hover:text-gray-900 mb-3">
        {t('detail.backLink')}
      </button>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-[26px] font-bold text-gray-900 tracking-tight leading-tight truncate">{req.item_description}</h1>
            <span className="text-sm font-mono text-gray-400 shrink-0">#{req.id}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLE[req.state]}`}>
              {t(`states.${req.state}`) || req.state}
            </span>
            {req.separada && req.state === 'approved' && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" title={req.separada_motivo || ''}>
                {t('detail.separada')}
              </span>
            )}
            {req.sap_modificada && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" title={t('detail.sapModificadaHint')}>
                ⚠ {t('detail.sapModificada')}
              </span>
            )}
            {pagada && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" title={t('detail.pagadaHint')}>
                {t('detail.pagada')}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400">
            {t('detail.updatedBy', { date: fmtDateTime(req.updated_at), name: req.requester?.display_name || '—' })}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={doDownload} disabled={busy}
            className="border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50"
            title={t('detail.actions.downloadTitle')}>
            {t('detail.actions.download')}
          </button>
          {(isPurchaser || isAdmin) && (
            <CopiarFormatoSap cargar={() => getFormatoSapSolicitud(req.id)} hecho={t('detail.actions.copiedSap')} className="font-medium" disabled={busy}>
              ⧉ {t('detail.actions.copySap')}
            </CopiarFormatoSap>
          )}
          {editing ? (
            <>
              <button onClick={saveAll} disabled={busy}
                className="bg-gray-900 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40">
                {t('common:actions.save')}
              </button>
              <button onClick={cancelEdit} disabled={busy}
                className="border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                {t('common:actions.cancel')}
              </button>
            </>
          ) : (
            <>
              {canEditData && (
                <button onClick={() => navigate(`/purchases/${req.id}/edit`)} disabled={busy}
                  className="border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {t('detail.actions.edit')}
                </button>
              )}
              {canEditOrder && (
                <button onClick={() => { startEdit(); setActiveTab('status') }} disabled={busy}
                  className="border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {t('detail.actions.editOrder')}
                </button>
              )}
              {canDelete && (
                <button onClick={doDelete} disabled={busy}
                  className="border border-gray-200 text-red-600 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-50">
                  {t('detail.actions.delete')}
                </button>
              )}
              {canSubmit && (
                <button onClick={doSubmit} disabled={busy}
                  className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40">
                  {t('detail.actions.submitForApproval')}
                </button>
              )}
              {canApproveReject && (
                <>
                  <button onClick={doApprove} disabled={busy}
                    className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-40">
                    {t('detail.actions.approve')}
                  </button>
                  <button onClick={() => setRejectModal(true)} disabled={busy}
                    className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-40">
                    {t('detail.actions.reject')}
                  </button>
                  {/* In the action bar itself, beside the buttons it informs. A
                      block above them left a band of empty page between the
                      figure and the decision it feeds. */}
                  <ApprovalBudget purchaseId={req.id} />
                </>
              )}
              {canSendToSap && (
                <button onClick={() => setSapModal(true)} disabled={busy}
                  className="bg-violet-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-40">
                  {t('detail.actions.uploadToSap')}
                </button>
              )}
              {canCancel && (
                <button onClick={() => setCancelModal(true)} disabled={busy}
                  className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-40">
                  {t('detail.actions.cancelOrder')}
                </button>
              )}
              {canOrder && (
                <button onClick={doOrder} disabled={busy}
                  className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40">
                  {t('detail.actions.markAsOrdered')}
                </button>
              )}
              {canReceive && (
                <button onClick={doReceive} disabled={busy}
                  className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-40">
                  {t('detail.actions.markReceived')}
                </button>
              )}
              {canClose && (
                <button onClick={doClose} disabled={busy}
                  className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40">
                  {t('detail.actions.close')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
      )}

      {req.state === 'rejected' && req.rejection_reason && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
          <strong>{t('detail.rejectedBanner')}</strong> {req.rejection_reason}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-5">
        {[
          { key: 'data', label: t('detail.tabs.data') },
          { key: 'status', label: t('detail.tabs.status') },
          { key: 'comments', label: t('detail.tabs.comments'), badge: comments.length || null },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => switchTab(tab.key)}
            className={`relative px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab.label}
            {tab.badge ? (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold align-middle">{tab.badge}</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Tab · Purchase data */}
      {activeTab === 'data' && (
        <>
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
            <Field label={t('detail.fields.project')}>
              <span className="font-medium">{req.project_reference || '—'}</span>
              {reqProject?.code && (
                <span className="ml-2 text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{reqProject.code}</span>
              )}
            </Field>
            <Field label={t('detail.fields.idRem')}>{req.is_multi ? <span className="text-gray-400 italic">{t('detail.fields.perItem')}</span> : (req.id_rem || '—')}</Field>
            <Field label={t('detail.fields.area')}>{req.area || '—'}</Field>
            <Field label={t('detail.fields.approvedBy')}>{req.approver?.display_name || '—'}</Field>
            <Field label={t('detail.fields.orderType')}>
              <div className="flex flex-wrap gap-1.5">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${req.is_multi ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'}`}>
                  {req.is_multi ? t('detail.fields.multi', { count: (req.items || []).length }) : t('detail.fields.singleItem')}
                </span>
                {req.order_type && (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${ORDER_TYPE_BADGE[req.order_type] || 'bg-gray-100 text-gray-700'}`}>
                    {t(`orderTypes.${req.order_type}Short`) || req.order_type}
                  </span>
                )}
                {!req.international && req.vat_included && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700">{t('detail.fields.vatIncluded')}</span>
                )}
                <span className="px-2 py-0.5 rounded-full text-[11px] font-mono bg-gray-100 text-gray-700" title={t('detail.fields.taxIndicatorHint')}>{req.international ? 'IVA_EXE' : 'IVA'}</span>
              </div>
            </Field>
            {!req.is_multi && (
              <>
                <Field label={t('detail.fields.quantity')}>{req.quantity}{req.unit ? ` ${req.unit}` : ''}</Field>
                <Field label={t('detail.fields.estUnitPrice')}>
                  {req.estimated_unit_price != null ? `${req.estimated_unit_price} ${req.currency}` : '—'}
                </Field>
              </>
            )}
            <Field label={t('detail.fields.supplier')}>{req.supplier || '—'}</Field>
            <Field label={t('detail.fields.url')}>
              {req.url
                ? <a href={externalHref(req.url)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{req.url}</a>
                : '—'}
            </Field>
            <div className="col-span-full">
              <Field label={t('detail.fields.justification')}>
                <p className="text-gray-700 whitespace-pre-wrap">{req.justification || '—'}</p>
              </Field>
            </div>
          </div>

          {/* Multi-item table */}
          {req.is_multi && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.multiItemsSection')} <span className="text-gray-400 font-normal">({(req.items || []).length})</span></h3>
              {(req.items || []).length === 0 ? (
                <p className="text-sm text-gray-400">{t('detail.noItems')}</p>
              ) : (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-[11px] uppercase text-gray-500 tracking-wider">
                        <th className="text-left font-semibold px-3 py-2">{t('detail.multiCols.num')}</th>
                        <th className="text-left font-semibold px-3 py-2">ID REM</th>
                        <th className="text-left font-semibold px-3 py-2">{t('detail.multiCols.description')}</th>
                        <th className="text-right font-semibold px-3 py-2">{t('detail.multiCols.qty')}</th>
                        <th className="text-left font-semibold px-3 py-2">{t('detail.multiCols.unit')}</th>
                        <th className="text-right font-semibold px-3 py-2">{t('detail.multiCols.unitPrice')}</th>
                        <th className="text-left font-semibold px-3 py-2">{t('detail.multiCols.curr')}</th>
                        <th className="text-right font-semibold px-3 py-2">{t('detail.multiCols.lineTotal')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(req.items || []).map((it, i) => {
                        const unitPrice = it.final_unit_price != null ? it.final_unit_price : it.estimated_unit_price
                        const lineCalc = computeLine(unitPrice, it.quantity, req.vat_included, req.international, req.tax_rate)
                        return (
                          <tr key={it.id || i} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-400 text-xs">{i + 1}</td>
                            <td className="px-3 py-2 text-gray-600 text-xs">{it.id_rem || '—'}</td>
                            <td className="px-3 py-2 text-gray-900">{it.item_description}</td>
                            <td className="px-3 py-2 text-right text-gray-700 tabular-nums">{it.quantity}</td>
                            <td className="px-3 py-2 text-gray-500 text-xs">{it.unit || '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-700 tabular-nums">{unitPrice != null ? unitPrice : '—'}</td>
                            <td className="px-3 py-2 text-gray-500 text-xs">{it.currency}</td>
                            <td className="px-3 py-2 text-right text-gray-900 font-medium tabular-nums">{lineCalc ? fmtMoney(lineCalc.total, it.currency) : '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Cost breakdown */}
          {(() => {
            const bd = lineTotals(req)
            const usingFinal = !req.is_multi && req.final_unit_price != null
            return (
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.costBreakdown.title')}</h3>
                {bd.mixed ? (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">{t('detail.costBreakdown.mixedCurrencies')}</p>
                    {Object.entries(bd.byCurr).map(([c, sums]) => (
                      <div key={c} className="grid grid-cols-3 gap-4 border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
                        <div>
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('detail.costBreakdown.subtotalFinal')} · {c}</p>
                          <p className="text-base text-gray-900">{fmtMoney(sums.subtotal, c)}</p>
                          {sums.discount > 0 && (
                            <p className="text-[11px] text-amber-600 mt-0.5">{t('detail.costBreakdown.discount', { amount: fmtMoney(sums.discount, c) })}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('detail.costBreakdown.vatPct', { pct: Math.round((bd.rate || 0) * 100) })}</p>
                          <p className="text-base text-gray-900">{fmtMoney(sums.vat, c)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('detail.costBreakdown.total')} · {c}</p>
                          <p className="text-base font-semibold text-gray-900">{fmtMoney(sums.total, c)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : bd.subtotal == null ? (
                  <p className="text-sm text-gray-400">{t('detail.costBreakdown.noPrice')}</p>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                        {t(`detail.costBreakdown.${usingFinal ? 'subtotalFinal' : 'subtotalEstimated'}`)}
                      </p>
                      <p className="text-base text-gray-900">{fmtMoney(bd.subtotal, bd.currency || req.currency)}</p>
                      {!req.is_multi && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {req.quantity}{req.unit ? ` ${req.unit}` : ''} × {fmtMoney(usingFinal ? req.final_unit_price : req.estimated_unit_price, req.currency)}
                        </p>
                      )}
                      {bd.discount > 0 && (
                        <p className="text-[11px] text-amber-600 mt-0.5">{t('detail.costBreakdown.discount', { amount: fmtMoney(bd.discount, bd.currency || req.currency) })}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                        {req.international ? t('detail.costBreakdown.vatIntl', { pct: Math.round((bd.rate || 0) * 100) }) : t('detail.costBreakdown.vatPct', { pct: Math.round((bd.rate || 0) * 100) })}
                      </p>
                      <p className="text-base text-gray-900">{fmtMoney(bd.vat, bd.currency || req.currency)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('detail.costBreakdown.total')}</p>
                      <p className="text-base font-semibold text-gray-900">{fmtMoney(bd.total, bd.currency || req.currency)}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
        </>
      )}

      {/* Tab · Status & logistics */}
      {activeTab === 'status' && !hasOrderInfo && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 mb-5 text-center text-sm text-gray-400">
          {t('detail.orderDetails.notAvailableYet')}
        </div>
      )}
      {activeTab === 'status' && hasOrderInfo && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.orderDetails.title')}</h3>
          {!(editing && canEditOrder) ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
              <Field label={t('detail.orderDetails.poNumber')}>{req.po_number || '—'}</Field>
              <Field label={t('detail.orderDetails.finalUnitPrice')}>
                {req.final_unit_price != null ? `${req.final_unit_price} ${req.currency}` : '—'}
              </Field>
              <Field label={t('detail.orderDetails.supplierConfirmed')}>{req.supplier || '—'}</Field>
              <Field label={t('detail.orderDetails.eta')}>{req.eta || '—'}</Field>
              <Field label={t('detail.orderDetails.tracking')}>{req.tracking || '—'}</Field>
              <Field label={t('detail.orderDetails.purchaser')}>{req.purchaser?.display_name || '—'}</Field>
              <Field label={t('detail.orderDetails.paymentStatus')}>
                <StatusPill
                  value={req.payment_status}
                  styleMap={PAYMENT_STYLE}
                  text={t(`board.paymentStatuses.${req.payment_status}`, { defaultValue: req.payment_status })}
                />
              </Field>
              <Field label={t('detail.orderDetails.purchaseDate')}>{req.purchase_date || '—'}</Field>
              <Field label={t('detail.orderDetails.leadtimeWeeks')}>{req.leadtime_weeks ?? '—'}</Field>
              {req.state === 'ordered' && (
                <Field label={t('detail.orderDetails.fulfillmentStatus')}>
                  <StatusPill
                    value={req.fulfillment_status}
                    styleMap={FULFILLMENT_STYLE}
                    text={t(`board.fulfillmentStatuses.${req.fulfillment_status}`, { defaultValue: req.fulfillment_status })}
                  />
                </Field>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('detail.orderDetails.poNumber')}</label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={orderForm.po_number} onChange={e => setOrderForm(f => ({ ...f, po_number: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('detail.orderDetails.finalUnitPrice')}</label>
                  <input type="number" min="0" step="any" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={orderForm.final_unit_price} onChange={e => setOrderForm(f => ({ ...f, final_unit_price: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('detail.orderDetails.eta')}</label>
                  <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={orderForm.eta} onChange={e => setOrderForm(f => ({ ...f, eta: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('detail.orderDetails.tracking')}</label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={orderForm.tracking} onChange={e => setOrderForm(f => ({ ...f, tracking: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('detail.orderDetails.paymentStatus')}</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={orderForm.payment_status} onChange={e => setOrderForm(f => ({ ...f, payment_status: e.target.value }))}>
                    {PAYMENT_STATUSES.map(s => (
                      <option key={s} value={s}>{t(`board.paymentStatuses.${s}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('detail.orderDetails.purchaseDate')}</label>
                  <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={orderForm.purchase_date} onChange={e => setOrderForm(f => ({ ...f, purchase_date: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('detail.orderDetails.leadtimeWeeks')}</label>
                  <input type="number" min="0" step="0.5" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={orderForm.leadtime_weeks} onChange={e => setOrderForm(f => ({ ...f, leadtime_weeks: e.target.value }))} />
                </div>
                {req.state === 'ordered' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('detail.orderDetails.fulfillmentStatus')}</label>
                    <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={orderForm.fulfillment_status} onChange={e => setOrderForm(f => ({ ...f, fulfillment_status: e.target.value }))}>
                      <option value="">{t('board.clear')}</option>
                      {FULFILLMENT_STATUSES.map(s => (
                        <option key={s} value={s}>{t(`board.fulfillmentStatuses.${s}`)}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Attachments (data tab) */}
      {activeTab === 'data' && (
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">{t('detail.attachments.title')}</h3>
          {canAttach && (
            <>
              <input ref={fileRef} type="file" hidden onChange={onFileChange} />
              <button onClick={onPickFile} disabled={busy}
                className="text-xs bg-gray-900 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-gray-700 disabled:opacity-40">
                {t('detail.actions.upload')}
              </button>
            </>
          )}
        </div>
        {(req.attachments || []).length === 0 ? (
          <p className="text-sm text-gray-400">{t('detail.attachments.noFiles')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {req.attachments.map(att => (
              <li key={att.id} className="flex items-center gap-3 py-2.5">
                <span className="flex-1 min-w-0">
                  <button
                    onClick={() => downloadPurchaseAttachment(req.id, att)}
                    className="text-sm text-gray-900 font-medium hover:underline truncate text-left"
                  >
                    {att.filename}
                  </button>
                  <span className="text-xs text-gray-400 ml-2">{fmtBytes(att.size_bytes)}</span>
                </span>
                <span className="text-xs text-gray-400 whitespace-nowrap">{fmtDateTime(att.uploaded_at)}</span>
                {(att.uploaded_by === user?.id || isAdmin) && (
                  <button
                    onClick={() => removeAttachment(att)}
                    className="text-xs text-gray-400 hover:text-red-500"
                    title={t('detail.attachments.remove')}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      {/* Comments (comments tab) */}
      {activeTab === 'comments' && ['approved', 'sap', 'ordered', 'received', 'closed'].includes(req.state) && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.comments.title')}</h3>
          {comments.length === 0 ? (
            <p className="text-sm text-gray-400">{t('detail.comments.noComments')}</p>
          ) : (
            <ul className="space-y-3 mb-4">
              {comments.map(c => (
                <li key={c.id} className="text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{c.author_name || 'Unknown'}</span>
                    <span className="text-xs text-gray-400">{fmtDateTime(c.created_at)}</span>
                    {c.tagged_user_name && (
                      <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">→ @{c.tagged_user_name}</span>
                    )}
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap mt-0.5"><TextoConMenciones texto={c.body} /></p>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-gray-100 pt-3 space-y-2">
              <MentionTextarea
                rows={3}
                placeholder={t('detail.comments.placeholder')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={commentBody}
                onChange={setCommentBody}
              />
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={commentTag}
                  onChange={e => setCommentTag(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <option value="">{t('detail.comments.tagMember')}</option>
                  {taggableUsers.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
                </select>
                <button
                  onClick={postComment}
                  disabled={postingComment || !commentBody.trim()}
                  className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40"
                >
                  {postingComment ? t('detail.actions.posting') : t('detail.actions.postComment')}
                </button>
              </div>
            </div>
        </div>
      )}

      {/* Comments not yet available (comments tab, pre-approval) */}
      {activeTab === 'comments' && !['approved', 'sap', 'ordered', 'received', 'closed'].includes(req.state) && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 mb-5 text-center text-sm text-gray-400">
          {t('detail.orderDetails.notAvailableYet')}
        </div>
      )}

      {activeTab === 'comments' && edits.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5 mb-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('detail.history.title')}</h2>
          <ul className="space-y-3">
            {edits.map(e => (
              <li key={e.id} className="text-sm">
                <div className="text-gray-500">
                  <span className="font-medium text-gray-700">{e.editor_name || '—'}</span>
                  {' · '}
                  {new Date(e.created_at).toLocaleString(currentLocale())}
                </div>
                <div className="text-gray-600 mt-0.5">
                  {(e.changes || []).map((c, i) => (
                    <span key={i} className="inline-block mr-3">
                      <span className="font-medium">{t('detail.history.fields.' + c.field, c.field)}</span>: {c.field === 'state' ? t('states.' + c.old, c.old ?? '—') : String(c.old ?? '—')} → {c.field === 'state' ? t('states.' + c.new, c.new ?? '—') : String(c.new ?? '—')}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setRejectModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">{t('detail.rejectModal.title')}</h2>
            <p className="text-xs text-gray-500 mb-4">
              {t('detail.rejectModal.hint')}
            </p>
            <textarea
              autoFocus
              rows={4}
              placeholder={t('detail.rejectModal.placeholder')}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
            />
            <div className="flex gap-3 mt-5">
              <button onClick={doReject} disabled={busy}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-40">
                {t('detail.rejectModal.confirm')}
              </button>
              <button onClick={() => setRejectModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200">
                {t('common:actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Upload to SAP: the SAP purchase order number is required */}
      {sapModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setSapModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">{t('detail.sapModal.title')}</h2>
            <p className="text-xs text-gray-500 mb-4">{t('detail.sapModal.hint')}</p>
            <input autoFocus value={sapPo} onChange={e => setSapPo(e.target.value)} placeholder={t('detail.sapModal.placeholder')}
              onKeyDown={e => { if (e.key === 'Enter' && sapPo.trim() && !busy) doSendToSap() }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" />
            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={doSendToSap} disabled={busy || !sapPo.trim()}
                className="flex-1 bg-violet-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-40">
                {t('detail.sapModal.confirm')}
              </button>
              <button onClick={() => setSapModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200">
                {t('common:actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Cancel modal */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setCancelModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">{t('detail.cancelModal.title')}</h2>
            <p className="text-xs text-gray-500 mb-4">{t('detail.cancelModal.hint')}</p>
            <textarea
              autoFocus
              rows={4}
              placeholder={t('detail.cancelModal.placeholder')}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
            />
            <div className="flex gap-3 mt-5">
              <button onClick={doCancel} disabled={busy || !cancelReason.trim()}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-40">
                {t('detail.cancelModal.confirm')}
              </button>
              <button onClick={() => setCancelModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200">
                {t('common:actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
